'use client';

import { imageService } from '@repo/ui/lib/services/image.service';
import { useCallback } from 'react';
import { useArbreImageOperations } from '../../../hooks/use-arbre-images';
import { ImageUpload } from '../../../shared/components/image-upload';
import { FormData } from '../../../shared/types';

interface ImageStepProps {
  formData: FormData;
  errors: { [key: string]: string };
  onChange: (fieldName: string, value: any) => void;
  arbreId?: number;
}

export function ImageStep({
  formData,
  errors,
  onChange,
  arbreId
}: ImageStepProps) {
  const imageOperations = useArbreImageOperations();
  const especePlaceholderSrc = formData._espece?.img_url
    ? imageService.getPrimaryImage(formData._espece.img_url, 'espece')
    : undefined;

  const imageConfig = {
    maxFiles: 1,
    acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'],
    maxSize: 20
  };

  // Enhanced onChange handler that can handle both file uploads and URL updates
  const handleImageChange = useCallback(async (fieldName: string, value: any) => {
    const field = fieldName as 'img1' | 'img2' | 'img3';

    try {
      if (arbreId && value instanceof File) {
        // Upload new image file to server
        const response = await imageOperations.upload.mutateAsync({
          arbreId,
          field,
          file: value
        });

        // Update form data with the new image path from server
        if (response.data.imgUrl && response.data.imgUrl[field]) {
          onChange(fieldName, response.data.imgUrl[field]);
        }
      } else if (arbreId && value === '') {
        // Delete image from server
        await imageOperations.delete.mutateAsync({
          arbreId,
          field
        });

        // Update form data to remove the image
        onChange(fieldName, '');
      } else {
        // Fallback to normal onChange for non-file values or when no arbreId
        onChange(fieldName, value);
      }
    } catch (error) {
      console.error(`Error handling image change for ${field}:`, error);
      // Still update form data even if server operation fails
      onChange(fieldName, value);
    }
  }, [arbreId, imageOperations.upload, imageOperations.delete, onChange]);

  // Handle image rotation
  const handleImageRotate = useCallback(async (fieldName: string, rotation: number) => {
    const field = fieldName as 'img1' | 'img2' | 'img3';

    if (!arbreId) {
      console.warn('Cannot rotate image: no arbreId provided');
      return;
    }

    try {
      const response = await imageOperations.rotate.mutateAsync({
        arbreId,
        field,
        rotation
      });

      // Update form data with the rotated image URL to force re-render
      // Add cache-busting parameter to force browser to reload the image
      if (response.data && response.data[field]) {
        const imageUrl = imageService.getImageUrl(response.data[field], 'arbre');
        const url = new URL(imageUrl, window.location.origin);
        url.searchParams.set('_t', Date.now().toString());
        onChange(fieldName, url.toString());
      }
    } catch (error) {
      console.error(`Error rotating image for ${field}:`, error);
      throw error; // Re-throw to let ImageUpload component handle the error display
    }
  }, [arbreId, imageOperations.rotate, onChange]);

  return (
    <div className="space-x-6 flex items-center">
      {/* Image principale */}
      <ImageUpload
        name="img1"
        label="Image principale"
        value={formData.img1 ? imageService.getImageUrl(formData.img1 || '', 'arbre') : ''}
        placeholderSrc={especePlaceholderSrc}
        onChange={handleImageChange}
        onRotate={handleImageRotate}
        required={false}
        imageConfig={imageConfig}
        error={errors.img1 || (imageOperations.error?.message)}
        isLoading={imageOperations.isLoading}
      />

      {/* Image secondaire */}
      <ImageUpload
        name="img2"
        label="Image secondaire"
        value={formData.img2 ? imageService.getImageUrl(formData.img2 || '', 'arbre') : ''}
        onChange={handleImageChange}
        onRotate={handleImageRotate}
        required={false}
        imageConfig={imageConfig}
        error={errors.img2 || (imageOperations.error?.message)}
        isLoading={imageOperations.isLoading}
      />

      {/* Image tertiaire */}
      <ImageUpload
        name="img3"
        label="Image tertiaire"
        value={formData.img3 ? imageService.getImageUrl(formData.img3 || '', 'arbre') : ''}
        onChange={handleImageChange}
        onRotate={handleImageRotate}
        required={false}
        imageConfig={imageConfig}
        error={errors.img3 || (imageOperations.error?.message)}
        isLoading={imageOperations.isLoading}
      />

      {/* Loading indicator */}
      {imageOperations.isLoading && (
        <div className="text-center py-2">
          <div className="inline-flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            <span className="text-sm text-muted-foreground">
              Traitement de l'image...
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
